import { WEB_BUILD } from "./buildInfo";

/*
 * The native apps keep one WebView alive for days, so a doctor can stay on an old release
 * long after a deploy. This compares the running build with /version.json when the app
 * opens and when it returns to the foreground, and reloads if they differ.
 *
 * A reload loses what is on screen, so a foreground check only reloads on the home screen.
 * A release that still does not match after reloading is never retried, which stops a
 * half-finished deploy from causing a reload loop.
 */

const RELOADED_FOR_KEY = "pwa_reloaded_for_version";
const SW_ACTIVATION_TIMEOUT_MS = 5000;

const fetchLiveVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) return null;
    const { version } = await response.json();
    return typeof version === "string" ? version : null;
  } catch {
    return null;
  }
};

/** Moves to the newest service worker, so the reload does not get old files from the old one. */
const activateLatestServiceWorker = async () => {
  const serviceWorker = navigator.serviceWorker;
  const registration = await serviceWorker?.getRegistration();
  if (!registration) return;
  await registration.update();
  if (!registration.installing && !registration.waiting) return;
  await new Promise<void>((resolve) => {
    serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
    setTimeout(resolve, SW_ACTIVATION_TIMEOUT_MS);
  });
};

export const reloadIfOutdated = async (canReload: () => boolean) => {
  const liveVersion = await fetchLiveVersion();
  if (!liveVersion || liveVersion === WEB_BUILD.version) return;
  if (!canReload() || sessionStorage.getItem(RELOADED_FOR_KEY) === liveVersion) return;

  sessionStorage.setItem(RELOADED_FOR_KEY, liveVersion);
  try {
    await activateLatestServiceWorker();
  } catch (error) {
    console.error("Service worker update failed:", error);
  }
  window.location.reload();
};

/** Native apps only: a browser tab already gets the latest release when it is opened. */
export const installVersionCheck = () => {
  if (!(window as any).ReactNativeWebView || WEB_BUILD.version === "dev") return;

  reloadIfOutdated(() => true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      reloadIfOutdated(() => window.location.pathname === "/");
    }
  });
};
